import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import FAQ from "@/components/FAQ";
import { STAGES, AUDIENCES, FAQ as FAQ_DATA, OPERATIONAL_PARTNERS } from "@/lib/siteContent";

const ARCH =
    "https://images.pexels.com/photos/30297108/pexels-photo-30297108.jpeg?auto=compress&cs=tinysrgb&w=2400";
const RIVIERA =
    "https://images.pexels.com/photos/14079666/pexels-photo-14079666.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function Approche() {
    return (
        <div data-testid="approche-page" className="bg-xv-ivory">
            {/* HERO */}
            <section className="relative pt-32 md:pt-44 pb-20 md:pb-28 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Approche · Méthodologie
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-black text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Quatre temps,{" "}
                            <em className="not-italic text-xv-midnight">
                                un accompagnement intégral.
                            </em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-3xl text-xv-text-secondary text-[17px] md:text-[19px] leading-[1.8] font-light">
                            XV Capital Partners conduit chaque opération
                            selon une méthodologie claire — du cadrage
                            initial à l'accompagnement post-closing — avec
                            un seul interlocuteur du début à la fin.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* FOUR STAGES — vertical editorial */}
            <section
                data-testid="stages"
                className="bg-xv-cream py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="space-y-px bg-xv-line">
                        {STAGES.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.06}>
                                <div
                                    data-testid={`stage-${i}`}
                                    className="bg-xv-cream"
                                >
                                    <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 py-14 md:py-20 px-2 lg:px-0">
                                        <div className="lg:col-span-3">
                                            <div
                                                className="font-serif text-[80px] md:text-[120px] lg:text-[160px] leading-none text-xv-midnight/15"
                                                style={{ fontWeight: 300 }}
                                            >
                                                {s.n}
                                            </div>
                                        </div>
                                        <div className="lg:col-span-4">
                                            <h2
                                                className="font-serif text-[32px] md:text-[40px] lg:text-[48px] text-xv-black leading-[1.05] mb-8"
                                                style={{ fontWeight: 400 }}
                                            >
                                                {s.title}
                                            </h2>
                                            <div className="border-t border-xv-gold/40 pt-4">
                                                <div className="overline text-xv-gold mb-3">
                                                    Objectif
                                                </div>
                                                <p
                                                    className="font-serif text-[17px] md:text-[19px] text-xv-midnight leading-snug"
                                                    style={{ fontWeight: 400 }}
                                                >
                                                    {s.objective}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="lg:col-span-5">
                                            <div className="overline text-xv-gold mb-6">
                                                Livrables clés
                                            </div>
                                            <ul className="space-y-4">
                                                {s.points.map((p, j) => (
                                                    <li
                                                        key={p}
                                                        className="flex items-start gap-5 border-t border-xv-line pt-4"
                                                    >
                                                        <span className="text-xv-gold text-[10px] tracking-[0.28em] mt-1">
                                                            {String(
                                                                j + 1
                                                            ).padStart(2, "0")}
                                                        </span>
                                                        <span className="text-xv-text-secondary text-[15px] md:text-[17px] leading-[1.85] font-light">
                                                            {p}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* AUDIENCES — clientele */}
            <section
                data-testid="audiences"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid lg:grid-cols-12 gap-12 mb-20">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                    <span className="divider-gold" />
                                    <span className="overline">
                                        Cœur de cible
                                    </span>
                                </div>
                                <h2
                                    className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[60px] leading-[1.04]"
                                    style={{ fontWeight: 300 }}
                                >
                                    Les profils{" "}
                                    <em className="not-italic text-xv-midnight">
                                        que nous accompagnons.
                                    </em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-6 lg:col-start-7 self-end">
                            <Reveal delay={0.1}>
                                <p className="text-xv-text-secondary text-[16px] leading-[1.85] font-light">
                                    Le cabinet sélectionne ses mandats. Nos
                                    interventions se concentrent sur des
                                    opérations exigeantes — où la justesse
                                    de la structuration vaut plus que la
                                    vitesse.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-px bg-xv-line">
                        {AUDIENCES.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.08}>
                                <div
                                    data-testid={`audience-${i}`}
                                    className="bg-xv-ivory p-10 md:p-14 h-full hover:bg-xv-cream transition-colors duration-700"
                                >
                                    <div className="flex items-start gap-6 mb-8">
                                        <span
                                            className="font-serif text-3xl text-xv-gold"
                                            style={{ fontWeight: 400 }}
                                        >
                                            {a.kicker}
                                        </span>
                                        <span className="block h-px w-12 bg-xv-gold mt-5" />
                                    </div>
                                    <h3
                                        className="font-serif text-[24px] md:text-[30px] text-xv-black mb-5 leading-tight"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {a.title}
                                    </h3>
                                    <p className="text-xv-text-secondary text-[15px] md:text-[16px] leading-[1.85] font-light max-w-md">
                                        {a.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* OPERATIONAL ECOSYSTEM */}
            <section
                data-testid="operational"
                className="bg-xv-cream py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <img
                                src={ARCH}
                                alt="Écosystème opérationnel"
                                className="w-full h-[420px] md:h-[600px] object-cover"
                            />
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:pl-8">
                        <Reveal>
                            <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                <span className="divider-gold" />
                                <span className="overline">
                                    Écosystème opérationnel
                                </span>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2
                                className="font-serif text-xv-black text-[36px] md:text-5xl lg:text-[56px] leading-[1.04] mb-10"
                                style={{ fontWeight: 300 }}
                            >
                                Avocats, fiscalistes,{" "}
                                <em className="not-italic text-xv-midnight">
                                    experts-comptables.
                                </em>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="text-xv-text-secondary text-[16px] md:text-[17px] leading-[1.85] font-light max-w-xl mb-12">
                                Pour chaque mandat, le cabinet mobilise
                                un sous-ensemble d'experts choisis parmi
                                un cercle indépendant — droit des
                                affaires, fiscalité, audit, due
                                diligence, conformité, commercial et
                                back-office.
                            </p>
                        </Reveal>
                        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
                            {OPERATIONAL_PARTNERS.map((p, i) => (
                                <Reveal key={p} delay={0.04 * (i % 6)}>
                                    <div
                                        data-testid={`partner-${i}`}
                                        className="flex items-start gap-4 border-t border-xv-line pt-4"
                                    >
                                        <span className="text-xv-gold text-[11px] tracking-[0.28em] mt-1">
                                            {String(i + 1).padStart(
                                                2,
                                                "0"
                                            )}
                                        </span>
                                        <span className="font-serif text-[17px] md:text-[19px] text-xv-black leading-snug">
                                            {p}
                                        </span>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FAQ
                items={FAQ_DATA}
                kicker="Questions fréquentes"
                title={
                    <>
                        Les questions{" "}
                        <em className="not-italic text-xv-midnight">
                            que l'on nous pose souvent.
                        </em>
                    </>
                }
            />

            {/* DISCRETION BAND */}
            <section
                data-testid="discretion"
                className="relative bg-xv-midnightDeep text-xv-ivory py-28 md:py-40 overflow-hidden"
            >
                <div className="absolute inset-0 opacity-15 pointer-events-none">
                    <img
                        src={RIVIERA}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-midnightDeep/80" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <div className="inline-flex items-center gap-4 mb-12 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">Engagement</span>
                            <span className="divider-gold" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-[36px] md:text-5xl lg:text-[64px] leading-[1.05] max-w-4xl mx-auto"
                            style={{ fontWeight: 300 }}
                        >
                            La discrétion absolue, comme socle{" "}
                            <em className="not-italic text-xv-goldSoft">
                                de toute relation.
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-10 text-xv-ivory/75 max-w-2xl mx-auto leading-[1.85] font-light">
                            Aucune mission, aucun nom, aucune opération
                            ne quitte le cabinet. Notre clientèle nous
                            recommande — c'est notre seule
                            communication.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <Link
                            to="/contact"
                            data-testid="approche-cta"
                            className="group mt-14 inline-flex items-center gap-4 px-10 py-5 bg-xv-ivory text-xv-midnightDeep text-[11px] tracking-[0.3em] uppercase hover:bg-xv-gold transition-all duration-700"
                        >
                            Demander un entretien confidentiel
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
